// Service location processing functions
import type { ManageAPI } from "~/manage-api/types";
import { debugLog } from '../store-layer/debugUtils';
import { 
  type ServiceLocationData
} from './serviceLocationTypes';
import { getServiceTypeById } from './serviceLocationUtils';

/**
 * Process all service locations and create data for each floor
 */
export function processAllServiceLocations(
  contextServiceLocations: ManageAPI.ServiceLocation[],
  floorsData: any[]
): {
  cachedServiceLocationsByFloor: Map<number, ServiceLocationData[]>,
  initialized: boolean
} {
  const cachedServiceLocationsByFloor = new Map<number, ServiceLocationData[]>();
  
  debugLog(`Processing ${contextServiceLocations.length} service locations from context`, 'serviceLocations');
  
  // Create a mapping of floor indexes to floor IDs from floors data
  const floorIndexToIdMap = new Map<number, number>();
  
  // Process floors data to create the mapping
  floorsData.forEach(floor => {
    if (floor.order !== undefined && floor.id !== undefined) {
      // Map floor order (which corresponds to floorIndex) to floor ID
      floorIndexToIdMap.set(floor.order, floor.id);
    }
  });
  
  debugLog('Floor index to ID mapping: ' + 
    Array.from(floorIndexToIdMap.entries())
      .map(([index, id]) => `Floor index ${index} → ID ${id}`)
      .join(', '), 'serviceLocations'
  );
  
  // Process each service location
  contextServiceLocations.forEach(serviceLocation => {
    if (serviceLocation.x === undefined || 
        serviceLocation.y === undefined || 
        serviceLocation.floorIndex === undefined ||
        serviceLocation.serviceType === undefined) {
      debugLog(`Skipping incomplete service location: ${JSON.stringify(serviceLocation)}`, 'serviceLocations');
      return;
    }
    
    // Get floor ID from the mapping based on floor index
    let floorId: number | null = null;
    
    if (floorIndexToIdMap.has(serviceLocation.floorIndex)) {
      floorId = floorIndexToIdMap.get(serviceLocation.floorIndex) || null;
    } else {
      debugLog(`No floor ID mapping found for floor index ${serviceLocation.floorIndex}`, 'serviceLocations');
      return;
    }
    
    // Skip if we couldn't determine the floor ID
    if (floorId === null) return;
    
    // Get service type information
    const serviceType = getServiceTypeById(serviceLocation.serviceType);
    if (!serviceType) {
      debugLog(`Unknown service type ID: ${serviceLocation.serviceType}`, 'serviceLocations');
      return;
    }
    
    // Create service location data
    const serviceData: ServiceLocationData = {
      id: serviceLocation.id,
      name: serviceType.name,
      code: serviceType.code,
      x: serviceLocation.x,
      y: serviceLocation.y,
      floorId: floorId,
      serviceType: serviceLocation.serviceType,
      originalData: serviceLocation
    };
    
    // Debug logging for raw coordinates
    debugLog(`Service location ${serviceLocation.id} (${serviceType.name}): Raw backend coords (${serviceLocation.x}, ${serviceLocation.y}) on floor ${floorId}`, 'serviceLocations');
    
    // Add to cache by floor
    if (!cachedServiceLocationsByFloor.has(floorId)) {
      cachedServiceLocationsByFloor.set(floorId, []);
    }
    cachedServiceLocationsByFloor.get(floorId)?.push(serviceData);
  });
  
  // Log what we found
  debugLog(`Pre-computed service locations for ${cachedServiceLocationsByFloor.size} floors:`, 'serviceLocations');
  cachedServiceLocationsByFloor.forEach((services, floorId) => {
    debugLog(`- Floor ${floorId}: ${services.length} service locations`, 'serviceLocations');
  });
  
  return {
    cachedServiceLocationsByFloor,
    initialized: true
  };
}

/**
 * Get service locations for a specific floor from cache
 */
export function getServiceLocationsForFloor(
  cachedServiceLocationsByFloor: Map<number, ServiceLocationData[]>,
  floorId: number
): ServiceLocationData[] {
  return cachedServiceLocationsByFloor.get(floorId) || [];
}

/**
 * Group service locations by service type
 */
export function groupServiceLocationsByType(
  serviceLocations: ServiceLocationData[]
): Map<number, ServiceLocationData[]> {
  const grouped = new Map<number, ServiceLocationData[]>();
  
  serviceLocations.forEach(service => {
    if (!grouped.has(service.serviceType)) {
      grouped.set(service.serviceType, []);
    }
    grouped.get(service.serviceType)?.push(service);
  });
  
  return grouped;
}

/**
 * Find service location by ID across all floors
 */
export function findServiceLocationById(
  cachedServiceLocationsByFloor: Map<number, ServiceLocationData[]>,
  serviceId: string
): (ServiceLocationData & { floorId: number }) | null {
  for (const [floorId, services] of cachedServiceLocationsByFloor.entries()) {
    const foundService = services.find(s => s.id.toString() === serviceId);
    if (foundService) {
      return { ...foundService, floorId };
    }
  }
  return null;
}

/**
 * Get statistics about service locations
 */
export function getServiceLocationStats(
  cachedServiceLocationsByFloor: Map<number, ServiceLocationData[]>
): {
  totalServiceLocations: number;
  serviceLocationsByFloor: Record<number, number>;
  serviceLocationsByType: Record<string, number>;
} {
  let totalServiceLocations = 0;
  const serviceLocationsByFloor: Record<number, number> = {};
  const serviceLocationsByType: Record<string, number> = {};
  
  for (const [floorId, services] of cachedServiceLocationsByFloor.entries()) {
    totalServiceLocations += services.length;
    serviceLocationsByFloor[floorId] = services.length;
    
    services.forEach(service => {
      const serviceType = getServiceTypeById(service.serviceType);
      if (serviceType) {
        const typeName = serviceType.name;
        serviceLocationsByType[typeName] = (serviceLocationsByType[typeName] || 0) + 1;
      }
    });
  }
  
  return {
    totalServiceLocations,
    serviceLocationsByFloor,
    serviceLocationsByType
  };
}

/**
 * Validate service location data
 */
export function validateServiceLocationData(
  serviceLocation: ManageAPI.ServiceLocation
): boolean {
  return (
    typeof serviceLocation.id === 'number' &&
    typeof serviceLocation.x === 'number' &&
    typeof serviceLocation.y === 'number' &&
    typeof serviceLocation.floorIndex === 'number' &&
    typeof serviceLocation.serviceType === 'number' &&
    !isNaN(serviceLocation.x) &&
    !isNaN(serviceLocation.y) &&
    serviceLocation.x >= 0 &&
    serviceLocation.y >= 0
  );
}

/**
 * Debug log service location processing information
 */
export function debugServiceLocationProcessing(
  cachedServiceLocationsByFloor: Map<number, ServiceLocationData[]>
): void {
  const stats = getServiceLocationStats(cachedServiceLocationsByFloor);
  
  debugLog(`Service Location Processing Summary:`, 'serviceLocations');
  debugLog(`- Total: ${stats.totalServiceLocations} service locations`, 'serviceLocations');
  debugLog(`- Floors: ${Object.keys(stats.serviceLocationsByFloor).length}`, 'serviceLocations');
  debugLog(`- Types: ${Object.keys(stats.serviceLocationsByType).length}`, 'serviceLocations');
  
  Object.entries(stats.serviceLocationsByType).forEach(([typeName, count]) => {
    debugLog(`  ${typeName}: ${count}`, 'serviceLocations');
  });
} 